/** @format */

import { Component, Input, Output, OnInit, EventEmitter, OnChanges, SimpleChanges, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import {
	Timesheet,
	TimesheetEntry2,
	EditTimesheetFormChanges,
	TimesheetEntryColors,
	TimesheetEntryType,
	BankedTypeOptions,
	TimesheetStatus,
	PostTimesheet,
	SaveSubmitTimesheet,
	TimesheetExceptionType,
	ExceptionType,
	TimeentryDB,
	TimesheetMetadata,
	EmploymentStatus,
} from 'src/app/store/application';
import { Configuration } from '../../models/profile';
import * as HelperFunctions from 'src/assets/helper-functions/helper-functions';
import { NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';

@Component({
	selector: 'app-entry-form',
	templateUrl: './entry-form.component.html',
	styleUrls: ['./entry-form.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class EntryFormComponent implements OnInit, OnChanges, OnDestroy {
	private ngUnsubscribe: Subject<void> = new Subject();

	weekdayString: Map<number, string> = new Map();
	totalHoursEntered: number[];
	remainingToAllocate: number[];
	showTooFewHoursError = false;
	showInvalidEntryError = false;
	emptyRows = false;
	lockTimeEntry = false;
	timesheet!: Timesheet;
	isSavedData = false;
	entryTypeToEntryTextMap: { [id: string]: string } = {};
	otToolTipInfo = '';

	@Input() selectedTimesheet!: Timesheet;
	@Input() weekOneTsHasUsedCdo = false;
	@Input() editOverride = false;
	@Input() currentConfiguration!: Configuration;
	@Input() selectedPayPeriodTimesheets: Timesheet[] = [];
	@Input() newEntryChanges!: EditTimesheetFormChanges;
	@Input() addedRows: TimesheetEntry2[] = [];
	@Input() timeEntryTypes: TimesheetExceptionType[] = [];
	@Input() isApprovalsView = false;
	@Input() exceptionTypes!: ExceptionType[];
	@Input() isEditingEmployeeTimesheet = false;

	@Output() openSidebar = new EventEmitter<boolean>();
	@Output() openEditSidebar = new EventEmitter<EditTimesheetFormChanges>();
	@Output() saveOrSubmitTimesheet = new EventEmitter<SaveSubmitTimesheet>();
	@Output() hasSubmittedTimesheetData = new EventEmitter<boolean>();
	@Output() needsToBlockApprovals = new EventEmitter<boolean>();

	constructor(private ngbConfig: NgbTooltipConfig) {
		this.totalHoursEntered = [0, 0, 0, 0, 0, 0, 0];
		this.remainingToAllocate = [0, 0, 0, 0, 0, 0, 0];

		this.ngbConfig.placement = 'bottom';
	}

	ngOnInit(): void {
		if (this.isApprovalsView) {
			// to show errors
			if (this.selectedTimesheet.status.toLowerCase() !== 'approved') {
				// this.showTooFewHoursError = !!this.remainingToAllocate.find(hours => hours > 0);
				this.showTooFewHoursError =
					// !this.selectedTimesheet.entries.some(e => !!e.metadata.cdoShiftNotes) &&
					!!this.remainingToAllocate.find((hours) => hours > 0) &&
					!this.isEditingEmployeeTimesheet &&
					!(this.currentConfiguration.employmentStatus === EmploymentStatus.AUX);
			}
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if ((changes.editOverride || changes.isApprovalsView) && !this.isEditingEmployeeTimesheet) {
			this.lockTimeEntry = this.editOverride;
			this.resetTimesheetToDefault();
		}

		if (changes.selectedTimesheet) {
			this.showTooFewHoursError = false;
			this.showInvalidEntryError = false;
			this.resetTimesheetToDefault();

			this.hasSubmittedTimesheetData.emit(this.hasSubmittedTimesheet());
			if (!this.isEditingEmployeeTimesheet) {
				this.lockTimeEntry = this.hasSubmittedTimesheet() || this.isApprovalsView;
			}
		}

		if (changes.addedRows && changes.addedRows.currentValue.length > 0) {
			const addedRow: TimesheetEntry2 = changes.addedRows.currentValue[0];
			this.setBaseEntryProperties(addedRow);
			this.timesheet.entries.push(addedRow);
		}

		if (changes.newEntryChanges && changes.newEntryChanges.currentValue && changes.newEntryChanges.currentValue.newEntry) {
			const replacementIndex = changes.newEntryChanges.currentValue.timeEntryIndexToReplace;
			const replacementRow = changes.newEntryChanges.currentValue.newEntry;
			this.setBaseEntryProperties(replacementRow);
			this.timesheet.entries[replacementIndex] = replacementRow;
		}

		if (changes.timeEntryTypes) {
			this.entryTypeToEntryTextMap = this.convertTimeEntryCodeToEntryTextMap();
			this.showInvalidEntryError = false;
			this.resetTimesheetToDefault();
		}

		this.updateTotals();
	}

	resetTimesheetToDefault() {
		const tempTimesheet: Timesheet = JSON.parse(JSON.stringify(this.selectedTimesheet));
		tempTimesheet.entries.forEach((entry) => this.setBaseEntryProperties(entry));
		this.timesheet = tempTimesheet;
	}

	trackByFn = (index: number) => {
		// console.log('day' + index + item)
		return 'day' + index;
	};

	convertTimeEntryCodeToEntryTextMap(): { [id: string]: string } {
		return this.timeEntryTypes.reduce((previousValue: { [id: string]: string }, currentValue: TimesheetExceptionType) => {
			previousValue[currentValue.code] = currentValue.entryText;
			return previousValue;
		}, {});
	}

	getEntryDisplayName(entry: TimesheetEntry2) {
		const entryText = this.nameToEntryText(entry);
		if (!!entryText) return entryText;

		const allExceptionTypes = HelperFunctions.getTimesheetExceptionTypes();
		switch (entry.name) {
			case TimesheetEntryType.ACTING:
				return allExceptionTypes.find((exType) => exType.code === TimesheetEntryType.ACTING)!.entryText;

			case TimesheetEntryType.BANKED:
				return allExceptionTypes.find((exType) => exType.code === TimesheetEntryType.BANKED)!.entryText;

			case TimesheetEntryType.CDO:
				return allExceptionTypes.find((exType) => exType.code === TimesheetEntryType.CDO)!.entryText;

			case TimesheetEntryType.COUNCIL:
				return allExceptionTypes.find((exType) => exType.code === TimesheetEntryType.COUNCIL)!.entryText;

			case TimesheetEntryType.OVERTIME:
				return allExceptionTypes.find((exType) => exType.code === TimesheetEntryType.OVERTIME)!.entryText;

			case TimesheetEntryType.REGULAR_DAY:
				return allExceptionTypes.find((exType) => exType.code === TimesheetEntryType.REGULAR_DAY)!.entryText;

			case TimesheetEntryType.SICK:
				return allExceptionTypes.find((exType) => exType.code === TimesheetEntryType.SICK)!.entryText;

			case TimesheetEntryType.STANDBY:
				return allExceptionTypes.find((exType) => exType.code === TimesheetEntryType.STANDBY)!.entryText;

			case TimesheetEntryType.STAT:
				return allExceptionTypes.find((exType) => exType.code === TimesheetEntryType.STAT)!.entryText;

			case TimesheetEntryType.TRAINING:
				return allExceptionTypes.find((exType) => exType.code === TimesheetEntryType.TRAINING)!.entryText;

			case TimesheetEntryType.UNION:
				return allExceptionTypes.find((exType) => exType.code === TimesheetEntryType.UNION)!.entryText;

			case TimesheetEntryType.VACATION:
				return allExceptionTypes.find((exType) => exType.code === TimesheetEntryType.VACATION)!.entryText;

			case TimesheetEntryType.OTHER:
				return allExceptionTypes.find((exType) => exType.code === TimesheetEntryType.OTHER)!.entryText;

			default:
				return 'Unknown Entry Type: ' + entry.name;
		}
	}

	nameToEntryText(entry: TimesheetEntry2) {
		const entryText = this.entryTypeToEntryTextMap[entry.name];
		// if (!entryText) {
		// 	for (const prop in this.entryTypeToEntryTextMap) {
		// 		if (this.entryTypeToEntryTextMap[prop] === entry.name) {
		// 			return this.entryTypeToEntryTextMap[prop];
		// 		}
		// 	}
		// }

		return entryText;
	}

	getEntryTypeColor = (entry: TimesheetEntry2) => HelperFunctions.getEntryTypeColor(entry);

	// getEntryTypeColor(entry: TimesheetEntry2) {
	//   let entryType = this.timeEntryTypes.find(entryType => entryType.code === entry.name || entryType.entryText === entry.name);
	//   return entryType?.colour || TimesheetEntryColors.OTHER;
	// }

	setBaseEntryProperties(entry: TimesheetEntry2) {
		const durationDisplayFormat: string[] = [];
		entry.durations.forEach((duration) => durationDisplayFormat.push(this.formatNumberDisplay(duration)));
		entry.durationsInDisplayFormat = durationDisplayFormat;
		entry.metadata.daysExceedingExpectedHours = []; // reset error states so they can be reevaluated each time
		entry.metadata.isInvalidCDOEntry = false;
		entry.metadata.isBankedTimeError = false;
		entry.metadata.isInvalidEntrySelection = !this.nameToEntryText(entry);

		this.showInvalidEntryError =
			Object.keys(this.entryTypeToEntryTextMap).length > 0 && (this.showInvalidEntryError ? true : !this.nameToEntryText(entry));

		this.needsToBlockApprovals.emit(this.isApprovalsView && this.showInvalidEntryError);
	}

	hasSubmittedTimesheet = (): boolean => this.timesheet.status !== TimesheetStatus.Predicted && this.timesheet.status !== TimesheetStatus.Saved;

	getTimesheetEntryDate = (timesheet: Timesheet, daysFromStart: number) =>
		HelperFunctions.getFutureDate(timesheet.startDate, daysFromStart).getDate();

	ngOnDestroy() {
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.complete();
	}

	formatNumberDisplay(number: number) {
		const formattedNum = Intl.NumberFormat(navigator.language, {
			minimumFractionDigits: 1,
			maximumFractionDigits: 2,
		}).format(number);

		return formattedNum;
	}

	isNotAllowedEdit() {
		return this.lockTimeEntry;
	}

	getTimesheetEntryColor = (entryName: string): TimesheetEntryColors => HelperFunctions.getTimesheetEntryColor(entryName);

	onAddRow() {
		this.openSidebar.emit(false);
	}

	onEditRow(entry: TimesheetEntry2, timesheetEntryIndex: number) {
		// if (this.lockTimeEntry || entry.weekEntries.some(dayEntry => dayEntry.shouldLockEntry)) {
		if (this.lockTimeEntry) {
			return;
		}

		const editChanges: EditTimesheetFormChanges = {
			previousEntry: entry,
			timeEntryIndexToReplace: timesheetEntryIndex,
		};

		this.openEditSidebar.emit(editChanges);
	}

	deleteRow(entryIndex: number) {
		// this.timeEntries = this.timeEntries.filter(timeEntry => timeEntry.exceptionType.id !== exTypeIdToDelete);
		this.timesheet.entries = this.timesheet.entries.filter((e, index) => index !== entryIndex);
		this.updateTotals();
	}

	convertToNumberAndUpdate(textEntry: string, entryIndex: number, weekDayIndex: number) {
		const number = Number(textEntry || '0.0');
		this.timesheet.entries[entryIndex].durations[weekDayIndex] = number;
		this.updateTotals();
	}

	updateTotals() {
		for (let i = 0; i < this.totalHoursEntered.length; i++) {
			this.totalHoursEntered[i] = 0;
		}

		for (const entry of this.timesheet.entries) {
			for (let i = 0; i < entry.durations.length; i++) {
				this.totalHoursEntered[i] += entry.durations[i];
			}
		}

		this.updateRemainingToAllocate();
	}

	handleFocusOut(entry: TimesheetEntry2, weekDayIndex: number) {
		if (entry.durations[weekDayIndex] === null || entry.durationsInDisplayFormat?.[weekDayIndex] === '') {
			entry.durations[weekDayIndex] = 0;
		}

		entry.durationsInDisplayFormat![weekDayIndex] = this.formatNumberDisplay(entry.durations[weekDayIndex]);
	}

	updateRemainingToAllocate() {
		const tempArr: number[] = [];
		for (let i = 0; i < this.remainingToAllocate.length; i++) {
			this.remainingToAllocate[i] = 0;
			tempArr.push(0);
		}

		for (const entry of this.timesheet.entries) {
			if (
				entry.name !== TimesheetEntryType.OVERTIME &&
				entry.name !== TimesheetEntryType.COUNCIL &&
				entry.name !== TimesheetEntryType.STANDBY
			) {
				for (let i = 0; i < entry.durations.length; i++) {
					tempArr[i] += entry.durations[i];
				}
			}
		}

		for (let i = 0; i < this.remainingToAllocate.length; i++) {
			const extendedDate = HelperFunctions.getFutureDate(this.timesheet.payPeriodStartDate, this.currentConfiguration.modifiedDurationDay - 1);
			const currentDate = HelperFunctions.getFutureDate(this.timesheet.startDate, i);
			const isExtendedDay = currentDate.getTime() === extendedDate.getTime();
			const expectedHours = this.timesheet.metadata[i].expectedDailyHours;
			// let expectedHours =
			// 	this.timesheet.metadata[i].expectedDailyHours === 0
			// 		? 0
			// 		: isExtendedDay
			// 		? this.currentConfiguration.durationOfModifiedDay
			// 		: this.currentConfiguration.durationOfRegularDay;

			this.remainingToAllocate[i] = Math.max(expectedHours - tempArr[i], 0);
		}
	}

	getRegularDayHours = () => this.currentConfiguration.durationOfRegularDay;
	getModifiedDayHours = () => this.currentConfiguration.durationOfModifiedDay;

	shouldShowError(): boolean {
		this.timesheet.entries.map((e) => {
			e.metadata.daysExceedingExpectedHours = [];
			e.metadata.isInvalidCDOEntry = false;
			e.metadata.isBankedTimeError = false;
			e.metadata.isInvalidEntrySelection = false;
			// e.metadata.cdoShiftNotes = "";
		});

		let showError = false;

		for (let currentDay = 0; currentDay < 7; currentDay++) {
			let totalRegHrsEntered = 0;

			for (const entry of this.timesheet.entries) {
				if (
					entry.name !== TimesheetEntryType.OVERTIME &&
					entry.name !== TimesheetEntryType.COUNCIL &&
					entry.name !== TimesheetEntryType.STANDBY
				) {
					if (this.currentConfiguration.employmentStatus !== EmploymentStatus.AUX) {
						totalRegHrsEntered += entry.durations[currentDay];
						if (totalRegHrsEntered > this.timesheet.metadata[currentDay].expectedDailyHours && entry.durations[currentDay] > 0) {
							entry.metadata.daysExceedingExpectedHours.push(currentDay + 1);
							showError = true;
						}

						// if the entry is CDO, check that the durations array contains the correct duration entry for it
						const isCDOError =
							entry.name === TimesheetEntryType.CDO &&
							entry.durations.some((duration) => duration !== 0 && duration !== this.getRegularDayHours());

						if (isCDOError) {
							entry.metadata.isInvalidCDOEntry = true;
							showError = true;
						}

						const customHoursExtractableBankTypes = [
							BankedTypeOptions.COT,
							BankedTypeOptions.POT,
							BankedTypeOptions.YOT,
							BankedTypeOptions.COUNCIL_OT,
						];

						if (
							entry.name === TimesheetEntryType.BANKED &&
							!customHoursExtractableBankTypes.includes(entry.metadata.options.bankedType)
						) {
							const isBankedTimeError = entry.durations.some((duration) => duration > 0 && duration % this.getRegularDayHours() !== 0);
							if (isBankedTimeError) {
								entry.metadata.isBankedTimeError = true;
								showError = true;
							}
						}
					}
				}

				if (!this.nameToEntryText(entry)) {
					entry.metadata.isInvalidEntrySelection = true;
					showError = true;
					this.showInvalidEntryError = true;
				}
			}
		}

		if (this.isEditingEmployeeTimesheet && !this.showInvalidEntryError) return false;
		if (this.currentConfiguration.employmentStatus === EmploymentStatus.AUX && !this.showInvalidEntryError) return false;

		this.showTooFewHoursError =
			this.currentConfiguration.employmentStatus !== EmploymentStatus.AUX && !!this.remainingToAllocate.find((hours) => hours > 0);

		return showError;
	}

	shouldShowBankCDODialog() {
		const isCDO = this?.currentConfiguration?.cdo;
		const tsMetadataHasCdoAllocated = this.selectedTimesheet.metadata.some((meta) => meta.isCDO);

		let showBankCDODialog = false;

		// the employee is part of the CDO program
		if (isCDO || tsMetadataHasCdoAllocated) {
			// const cdoDay = this.currentConfiguration.cdoDay - 1;
			// const cdoDate = HelperFunctions.getFutureDate(new Date(this.timesheet.payPeriodStartDate), cdoDay);

			// const currentTimesheetExpectsCDO = HelperFunctions.getDateIsWithinBounds(
			// 	new Date(this.timesheet.startDate),
			// 	new Date(this.timesheet.endDate),
			// 	cdoDate
			// );

			// if (currentTimesheetExpectsCDO) {
			if (tsMetadataHasCdoAllocated) {
				// CDO time is booked during the week
				const isUsingCDO = this.timesheet.entries.some(
					(entry) => this.entryIsCDO(entry) && entry.durations.some((duration) => duration === this.getRegularDayHours()),
				);

				showBankCDODialog = !isUsingCDO && !this.weekOneTsHasUsedCdo;
			}
		}

		return showBankCDODialog;
	}

	entryIsCDO(entry: TimesheetEntry2) {
		let isEntryCDO = false;
		if (entry.name === TimesheetEntryType.CDO) {
			isEntryCDO = true;
		} else if (entry.name === TimesheetEntryType.BANKED) {
			const bankedType = entry?.metadata?.options?.bankedType;
			if (bankedType === BankedTypeOptions.CDO || bankedType === BankedTypeOptions.STAT_CDO) {
				isEntryCDO = true;
			}
		}

		return isEntryCDO;
	}

	cleanInput(event: any, isPasteEvent: boolean, entry: TimesheetEntry2, arr: any[], weekDayIndex: number) {
		HelperFunctions.textInputCleanup(event, isPasteEvent, arr, weekDayIndex, [
			this.updateTotals.bind(this),
			// this.handleFocusOut.bind(this, entry, weekDayIndex)
		]);
	}

	isDayLongerThanZeroHours(entry: TimesheetEntry2, weekDayIndex: number) {
		return entry.durations[weekDayIndex] == 0;
	}

	handleUndoBankCDO() {
		const cdoDayIndex = this.currentConfiguration.cdoDay <= 7 ? this.currentConfiguration.cdoDay - 1 : this.currentConfiguration.cdoDay - 8;
		const durations = Array(7).fill(0);
		durations[cdoDayIndex] = this.timesheet.metadata[cdoDayIndex].expectedDailyHours;

		const cdoRow: TimesheetEntry2 = {
			name: HelperFunctions.getTimesheetExceptionTypes().find((exType) => exType.code === 'CDO')!.code,
			metadata: {},
			durations,
			durationsInDisplayFormat: [],
		};

		this.setBaseEntryProperties(cdoRow);

		this.timesheet.entries.forEach((entry) => {
			entry.durations[cdoDayIndex] = 0;
			entry.durationsInDisplayFormat![cdoDayIndex] = '0.0';
		});

		if (this.timesheet.entries.some((entry) => entry.name === TimesheetEntryType.CDO)) {
			const indexOfCDO = this.timesheet.entries.findIndex((entry) => entry.name === TimesheetEntryType.CDO);
			this.timesheet.entries[indexOfCDO] = cdoRow;
		} else {
			this.timesheet.entries.push(cdoRow);
		}

		delete this.timesheet.metadata[cdoDayIndex].bankedCDO;
		this.updateTotals();
	}

	processTimesheet(action: string): void {
		const timesheetMetadata = this.timesheet.metadata;
		const cdoBankPresent = timesheetMetadata.some((m) => m.bankedCDO);
		const cdoPresent = this.timesheet.entries.some(
			(e) => e.name === TimesheetEntryType.CDO && e.durations.some((d) => d === this.getRegularDayHours()),
		);

		if (cdoPresent && cdoBankPresent) {
			const bankedMetadataIndex = timesheetMetadata.findIndex((m) => m.bankedCDO);
			delete timesheetMetadata[bankedMetadataIndex].bankedCDO;
		}

		const newTimesheet: PostTimesheet = {
			action,
			employeeNumber: '',
			payPeriod: this.selectedTimesheet.payPeriod,
			timesheetMetadata: JSON.stringify(timesheetMetadata),
			entryData: JSON.stringify(this.timesheet.entries),
		};

		const isEdited = this.compareTS(this.selectedTimesheet.entries, this.timesheet.entries);

		const startDate = new Date(this.selectedTimesheet.startDate);
		const year = startDate.getFullYear();
		const month = HelperFunctions.convertSingleConstToDoubleDigitStr(startDate.getMonth() + 1);
		const day = HelperFunctions.convertSingleConstToDoubleDigitStr(startDate.getDate());

		const startDateString = `${year}${month}${day}`;

		// let isEdited = this.compareTS(this.selectedTimesheet.entries, this.timesheet.entries);
		if (action === 'save') {
			this.saveOrSubmitTimesheet.emit({
				actionType: action,
				isShowBankCDODialog: false,
				timesheet: newTimesheet,
				startDate: startDateString,
				isEdited: isEdited,
			});

			return;
		}

		this.emptyRows = this.timesheet.entries.length === 0;

		// this.showTooFewHoursError = !!this.remainingToAllocate.find(hours => hours > 0) && !this.isEditingEmployeeTimesheet && !(this.currentConfiguration.employmentStatus === EmploymentStatus.AUX);
		const showBankCDODialog = this.shouldShowBankCDODialog();
		const hasRowEntryError = this.shouldShowError();

		if (!this.showTooFewHoursError && !hasRowEntryError && !this.emptyRows) {
			// isEdited = this.compareTS(this.selectedTimesheet.entries, this.timesheet.entries);
			// newTimesheet.entryData = JSON.stringify(this.timesheet.entries);

			this.saveOrSubmitTimesheet.emit({
				actionType: action,
				isShowBankCDODialog: showBankCDODialog,
				timesheet: newTimesheet,
				startDate: startDateString,
				isEdited: isEdited,
			});

			if (!showBankCDODialog) {
				this.hasSubmittedTimesheetData.emit(true);
				this.lockTimeEntry = true;
			}
		}
	}

	getToolTip(entryIndex: number, weekDayIndex: number) {
		const en = this.timesheet.entries[entryIndex];
		let otEntries: TimeentryDB[] = [];
		const duration = this.timesheet.entries[entryIndex].durations[weekDayIndex];

		if (en.name === 'Overtime') {
			otEntries = HelperFunctions.otTimeEntries(this.timesheet, en, weekDayIndex, this.exceptionTypes, this.currentConfiguration);
		} else if (en.name === 'Council') {
			otEntries = HelperFunctions.councilTimeEntries(this.timesheet, en, weekDayIndex, this.exceptionTypes, this.currentConfiguration);
		}

		if (duration !== 0 && otEntries.length > 0) {
			this.otToolTipInfo = ``;

			if (en.name === 'Council') {
				this.otToolTipInfo += `<table><tr><td>Council 0.5</td><td>&nbsp;&nbsp;</td><td>${this.formatNumberDisplay(
					otEntries[0].duration,
				)} hrs</td></tr>\n`;
			} else {
				otEntries
					.filter((ot) => ot.comments.indexOf('Meal') !== 0)
					.forEach((ot) => {
						this.otToolTipInfo += `<table><tr><td>${ot.comments}</td><td>&nbsp;&nbsp;</td><td>${this.formatNumberDisplay(
							ot.duration,
						)} hrs</td></tr>\n`;
					});

				if (otEntries.filter((ot) => ot.comments.indexOf('Meal') >= 0)) {
					const subTotal = otEntries.filter((ot) => ot.comments.indexOf('Meal') !== 0).reduce((sum, current) => sum + current.duration, 0);
					this.otToolTipInfo += `<tr><td><b>Hours Worked</b></td><td>&nbsp;&nbsp;</td><td><b>${this.formatNumberDisplay(
						subTotal,
					)} hrs</b></td></tr>`;
				}

				otEntries
					.filter((ot) => ot.comments.indexOf('Meal') >= 0)
					.forEach((ot) => {
						this.otToolTipInfo += `<tr><td>${ot.comments}</td><td>&nbsp;&nbsp;</td><td>${this.formatNumberDisplay(
							ot.duration,
						)} hrs</td></tr>\n`;
					});
			}
			const total = otEntries.reduce((sum, current) => sum + current.duration, 0);
			this.otToolTipInfo += `<tr><td><b>TOTAL</b></td><td>&nbsp;&nbsp;</td><td><b>${this.formatNumberDisplay(total)} hrs</b></td></tr></table>`;
		}

		return this.otToolTipInfo;
	}

	compareTS(entries: TimesheetEntry2[], entries1: TimesheetEntry2[]): boolean {
		let edited = false;
		if (entries.length !== entries1.length) {
			edited = true;
		} else {
			for (let i = 0; i < entries.length; i++) {
				const entryCompare = entries1.filter((e) => e.name.toLowerCase() === entries[i].name.toLowerCase());
				if (entryCompare.length > 0) {
					for (let x = 0; x < entries[i].durations.length; x++) {
						if (entries[i].durations[x] !== entryCompare[0].durations[x]) {
							edited = true;
							break;
						}
					}
				}
			}
		}

		return edited;
	}

	// currentWeekYYYYPPWW(): string {
	//   let yyyypp, ww = '';
	//   const payPeriodStartDate = this.selectedTimesheet.payPeriodStartDate;
	//   if (payPeriodStartDate) {
	//     yyyypp = this.selectedTimesheet.payPeriod;
	//     ww = HelperFunctions.getDifferenceInDays(payPeriodStartDate, this.selectedTimesheet.startDate) === 0 ? '01' : '02';
	//   }

	//   return yyyypp + ww;
	// }

	isTimesheetApproved = (): boolean =>
		this.selectedTimesheet.status === TimesheetStatus.Approved || this.selectedTimesheet.status === TimesheetStatus.JDE;
}
